import type { RacePhase, RewardMsg } from '@runkr/schema'
import type { ComponentType } from 'preact'
import type { ResultRow } from '../net/roomSnapshot'
import type { Flow } from '../ui/flow'
import type { ProfileApi } from './profileApi'
import { ProfileStore } from './profileStore'
import { RaceRewards } from './raceRewards'
import { makeRewardLines } from './RewardLines'

/** The bits of the session's room data the progress code reads. */
export interface ProgressRoom {
  phase: RacePhase
  results: ResultRow[]
  mySessionId: string
}

/** Read side of a `Store` (covariant, so `Store<RoomInfo>` fits `Readable<ProgressRoom>`). */
export interface Readable<T> {
  get(): T
  subscribe(listener: (value: T) => void): () => void
}

export interface ProgressDeps {
  /** `AuthSession.profile` once sign-in settles. */
  api: Promise<ProfileApi | null>
  onReward: (listener: (msg: RewardMsg) => void) => () => void
  flow: Readable<Flow>
  room: Readable<ProgressRoom | null>
}

/**
 * Phase 2 progress on the session (T2.4): the profile store, the last race's rewards and the
 * `RewardLines` component. Feeds itself from the room (own result row), the `"reward"`
 * message and the flow (a race played without a reward re-reads the profile on the menu).
 */
export class Progress {
  readonly profile: ProfileStore
  readonly rewards: RaceRewards
  readonly RewardLines: ComponentType
  /** A race started and no reward has come since: re-read the profile on the menu. */
  private stale = false
  private lastScreen: Flow['screen']

  constructor(deps: ProgressDeps) {
    this.profile = new ProfileStore(deps.api)
    this.rewards = new RaceRewards(() => this.profile.status.get() === 'offline')
    this.RewardLines = makeRewardLines(this.rewards.view)
    this.lastScreen = deps.flow.get().screen
    deps.onReward((msg) => this.onReward(msg))
    deps.room.subscribe((room) => this.onRoom(room))
    deps.flow.subscribe((flow) => this.onFlow(flow))
  }

  private onReward(msg: RewardMsg): void {
    this.stale = false
    this.rewards.reward(msg)
    this.profile.applyReward(msg)
  }

  private onRoom(room: ProgressRoom | null): void {
    if (!room) return
    if (room.phase === 'finished') {
      this.rewards.results(room.results.find((r) => r.sessionId === room.mySessionId) ?? null)
      return
    }
    // A new lobby or a rematch: the last race's lines go. Not on `racing`: a late racing
    // snapshot (the `?lag` buffer) must not drop a reward that arrived before it.
    if (room.phase === 'racing') this.stale = true
    else this.rewards.reset()
  }

  private onFlow(flow: Flow): void {
    const from = this.lastScreen
    this.lastScreen = flow.screen
    if (flow.screen !== 'menu' || from === 'menu' || from === 'profile') return
    if (!this.stale) return
    this.stale = false
    void this.profile.refresh()
  }
}
